import { type FormEvent, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import {
  isElectronDesktop,
  refreshDesktopSettingsCache,
  type DesktopSettings,
} from "../desktop";
import { useToast } from "../components/Toast";
import { useStashEnabled } from "../hooks/useStashEnabled";

type LocalForm = Pick<
  DesktopSettings,
  "octoApiToken" | "octoLocalApi" | "socks5Proxy" | "collectorApiUrl"
>;

const EMPTY: LocalForm = {
  octoApiToken: "",
  octoLocalApi: "http://127.0.0.1:58888/api/profiles/",
  socks5Proxy: "",
  collectorApiUrl: "http://127.0.0.1:3210",
};

/** Desktop-only: local Octo + SOCKS5. No server configuration. */
export function SettingsPage() {
  const { push } = useToast();
  const desktop = isElectronDesktop();
  const [form, setForm] = useState<LocalForm>(EMPTY);
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const { stashEnabled, setStashEnabled } = useStashEnabled();

  useEffect(() => {
    if (!desktop || !window.brexDesktop) return;
    void window.brexDesktop
      .getSettings()
      .then((s) => {
        setForm({
          octoApiToken: s.octoApiToken,
          octoLocalApi: s.octoLocalApi,
          socks5Proxy: s.socks5Proxy,
          collectorApiUrl: s.collectorApiUrl || "http://127.0.0.1:3210",
        });
        setLoaded(true);
      })
      .catch((err) => {
        push(err instanceof Error ? err.message : "Failed to load settings", "error");
        setLoaded(true);
      });
  }, [desktop, push]);

  if (!desktop) {
    return <Navigate to="/" replace />;
  }

  async function onSave(e: FormEvent) {
    e.preventDefault();
    if (!window.brexDesktop) return;
    setBusy(true);
    try {
      const result = await window.brexDesktop.saveSettings({
        octoApiToken: form.octoApiToken,
        octoLocalApi: form.octoLocalApi,
        socks5Proxy: form.socks5Proxy,
        collectorApiUrl: form.collectorApiUrl,
      });

      if (!result.ok) {
        push(result.error, "error");
        return;
      }

      setForm({
        octoApiToken: result.settings.octoApiToken,
        octoLocalApi: result.settings.octoLocalApi,
        socks5Proxy: result.settings.socks5Proxy,
        collectorApiUrl: result.settings.collectorApiUrl || "http://127.0.0.1:3210",
      });
      await refreshDesktopSettingsCache();
      push("Local desktop settings saved", "ok");
    } catch (err) {
      push(err instanceof Error ? err.message : "Save failed", "error");
    } finally {
      setBusy(false);
    }
  }

  function setField<K extends keyof LocalForm>(key: K, value: LocalForm[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1>Desktop settings</h1>
          <p className="muted">Local OctoBrowser and SOCKS5 only — server config is admin-only</p>
        </div>
      </header>

      {!loaded ? (
        <p className="muted">Loading…</p>
      ) : (
        <>
          <form className="settings-form surface" onSubmit={(e) => void onSave(e)}>
            <fieldset>
              <legend>OctoBrowser</legend>
              <label>
                <span>Octo API key</span>
                <input
                  type="text"
                  autoComplete="off"
                  value={form.octoApiToken}
                  onChange={(e) => setField("octoApiToken", e.target.value)}
                  placeholder="Octo automation token"
                />
              </label>
              <label>
                <span>Local Octo API</span>
                <input
                  value={form.octoLocalApi}
                  onChange={(e) => setField("octoLocalApi", e.target.value)}
                  placeholder="http://127.0.0.1:58888/api/profiles/"
                />
                <span className="muted tiny">Always direct — never routed through SOCKS5</span>
              </label>
            </fieldset>

            <fieldset>
              <legend>Stash</legend>
              <label className="stash-setting">
                <span>Auto-stash finished logins</span>
                <button
                  type="button"
                  className={`stash-toggle${stashEnabled ? " is-on" : ""}`}
                  onClick={() => {
                    void setStashEnabled(!stashEnabled).catch((err) =>
                      push(err instanceof Error ? err.message : "Could not update stash", "error")
                    );
                  }}
                >
                  {stashEnabled ? "On" : "Off"}
                </button>
                <span className="muted tiny">
                  When on, enrichment-ready admin accounts go to Stash → Pending
                  immediately. Employee and Medium always land on their own lists
                  regardless of this toggle. When off, finished admin logins stay
                  unsorted until you move them.
                </span>
              </label>
            </fieldset>

            <fieldset>
              <legend>Collector</legend>
              <label>
                <span>Collector API</span>
                <input
                  value={form.collectorApiUrl || ""}
                  onChange={(e) => setField("collectorApiUrl", e.target.value)}
                  placeholder="http://127.0.0.1:3210"
                />
                <span className="muted tiny">
                  Local only. Desktop does not accept collector pushes — harvest
                  goes Collector → hosted hub, and this app connects out to that hub.
                </span>
              </label>
            </fieldset>

            <fieldset>
              <legend>Desktop proxy</legend>
              <label>
                <span>SOCKS5 proxy</span>
                <input
                  value={form.socks5Proxy}
                  onChange={(e) => setField("socks5Proxy", e.target.value)}
                  placeholder="socks5://user:pass@host:port"
                />
                <span className="muted tiny">
                  Required for the hosted hub and any remote collector URL — Electron
                  websockets and fetches go through this SOCKS. Localhost and local Octo
                  stay direct. Also set on the launch connect screen (persisted).
                </span>
              </label>
            </fieldset>

            <div className="settings-actions">
              <button type="submit" disabled={busy}>
                {busy ? "Saving…" : "Save settings"}
              </button>
            </div>
          </form>
        </>
      )}
    </div>
  );
}
